import "@phosphor-icons/web/bold/style.css";
import "@phosphor-icons/web/duotone/style.css";
import "@phosphor-icons/web/fill/style.css";
import "@phosphor-icons/web/light/style.css";
import "@phosphor-icons/web/regular/style.css";
import "@phosphor-icons/web/thin/style.css";

import "@fontsource/dm-mono/400.css";
import "@fontsource/dm-mono/500.css";
import "@fontsource/dm-sans/300.css";
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/600.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";

import "./styles/alerts.css";
import "./styles/aoiMap.css";
import "./styles/aplicacion.css";
import "./styles/aplicacionForm.css";
import "./styles/app.css";
import "./styles/autocomplete.css";
import "./styles/badges.css";
import "./styles/buttons.css";
import "./styles/card.css";
import "./styles/clima.css";
import "./styles/components.css";
import "./styles/crearFincaPage.css";
import "./styles/detail.css";
import "./styles/echart.css";
import "./styles/error.css";
import "./styles/farmTypeSelectorField.css";
import "./styles/fertilizacion.css";
import "./styles/forms.css";
import "./styles/fsForm.css";
import "./styles/fsModal.css";
import "./styles/index.css";
import "./styles/list.css";
import "./styles/loteAssign.css";
import "./styles/loteInfo.css";
import "./styles/loteSelectorField.css";
import "./styles/loteSplit.css";
import "./styles/mainLayout.css";
import "./styles/section.css";
import "./styles/map.css";
import "./styles/modal.css";
import "./styles/monitoreoAnalisis.css";
import "./styles/monitoreoBroca.css";
import "./styles/monitoreoConfiguracion.css";
import "./styles/monitoreoReporte.css";
import "./styles/monitoreoSesion.css";
import "./styles/pesajeGanaderiaDashboard.css";
import "./styles/proyectos.css";
import "./styles/rastro.css";
import "./styles/secondSidebar.css";
import "./styles/rotacionPesajeVeterinariaForm.css";
import "./styles/summary.css";
import "./styles/tokens.css";
import "./styles/utils.css";
import "./styles/ventaForm.css";
import "./styles/yearGroupSelector.css";

import { BaseLoginResponse } from "./base/response/BaseLoginResponse";
import { AppConfig } from "./components/definitions/app/AppConfig";
import { IAsideMenuRender } from "./components/definitions/app/IAsideMenuRender";
import { AppRender } from "./components/implementations/app/AppRender";
import { syncAllCatalogsInBackground } from "./comun/services/catalogSync";
import { errorService } from "./comun/services/ErrorService";
import { grpcTransportProvider } from "./comun/services/grpc/GrpcTransport";
import { Login } from "./components/modulos/seguridad/login/Login";
import { fx_createLoginByTokenRequest } from "./modulos/seguridad/login/request/LoginByTokenRequest";
import { LoginByTokenGrpcService } from "./modulos/seguridad/login/services/LoginByTokenGrpcService";
import { secureStorage } from "./modulos/seguridad/login/services/SecureStorageService";
import { LoginPortalGrpcService } from "./modulos/seguridad/login/services/LoginPortalGrpcService";
import { HttpError } from "./comun/services/api/error/HttpError";
import { NotFoundError } from "./comun/services/api/error/NotFoundError";
import { UnauthorizedError } from "./comun/services/api/error/UnauthorizedError";
import {
  fx_irAlPortalNitria,
  fx_renderAccesoPortal,
  PORTAL_NITRIA_BOTON,
  PORTAL_NITRIA_REINTENTAR_MESSAGE,
  PORTAL_NITRIA_RUTA_SSO,
  PORTAL_NITRIA_SIN_CONEXION_MESSAGE,
  PORTAL_NITRIA_SIN_TOKEN_MESSAGE,
} from "./comun/portalNitria";

/** Modo de ejecución: define qué ruta base de API se usa, y permite ocultar en
 *  producción módulos todavía sin terminar. */
export enum RunModes {
  /** Local (`http://localhost:5153`). */
  Debug,
  /** Entorno de testing (`InitData.testingBaseApiRoute`). */
  Testing,
  /** Producción (`InitData.releaseBaseApiRoute`). */
  Release,
}

/** Modo del build inyectado por `build.js` vía `define` de esbuild
 *  (`"dev" | "test" | "prod"`). */
declare const __RUN_MODE__: string;

/** Modo activo de la app, derivado del modo del build (`npm run dev/test/build`).
 *  Sin ediciones manuales: cada build sale apuntando a su entorno. */
export const RunMode: RunModes =
  __RUN_MODE__ === "prod"
    ? RunModes.Release
    : __RUN_MODE__ === "test"
      ? RunModes.Testing
      : RunModes.Debug;

/** Inicializa el transporte gRPC-Web con la ruta base de la marca según
 *  `RunMode`. Lo usan el arranque de la app (`BaseInit.init`) y la página
 *  oculta de ingreso con contraseña (`fx_initLoginInterno`). */
export function fx_initGrpcTransport(data: InitData): void {
  const apiBaseRoute =
    RunMode === RunModes.Debug
      ? "http://localhost:5153"
      : RunMode === RunModes.Testing
        ? data.testingBaseApiRoute
        : data.releaseBaseApiRoute;

  grpcTransportProvider.initBaseRoute(apiBaseRoute);
}

/** Datos de configuración por marca/entorno que recibe `BaseInit`. */
export type InitData = {
  /** Ruta base de API en testing. */
  testingBaseApiRoute: string;
  /** Ruta base de API en release. */
  releaseBaseApiRoute: string;
  /** Ruta del logo (loader/login). */
  logoRoute: string;
  /** Ruta del brand (topbar/sidebar). */
  brandRoute: string;
  /** Título por defecto del topbar. */
  defaultTitle: string;
  /** Id de marca (`"sextante"` | `"nitrofert"` | `"agro360"`) — se refleja en
   *  `<body data-brand="...">` para poder escribir CSS exclusivo de una
   *  marca (`[data-brand="agro360"] .foo {...}`) sin afectar a las demás. */
  brandId: string;
  /** Si el login ofrece la recuperación de contraseña ("¿Olvidaste tu
   *  contraseña?"). Hoy solo Sextante la tiene; en las demás marcas el
   *  flujo existe pero no se expone. */
  withRecuperarPassword: boolean;
  /** URL del login del portal de Nitria. Si viene, la marca entra solo por
   *  el portal (SSO, decidido 2026-09-30): la app atiende la ruta del
   *  portal, sin sesión válida redirige a esta URL y nunca muestra su
   *  pantalla de login. El ingreso con contraseña queda en una página oculta
   *  aparte (`fx_initLoginInterno`). Hoy solo la pasa Nitrofert. */
  portalLoginUrl?: string;
};

/** Clase base de inicialización de la app: arma el provider de servicios, el
 *  loader, valida el token de sesión y monta el layout (o el login). Las
 *  subclases (p. ej. `ClienteInit`) aportan las rutas de login y el menú. */
export abstract class BaseInit<TLoginResponse extends BaseLoginResponse> {
  /** instancia del AsideMenuRender */
  protected abstract asideMenuRender: IAsideMenuRender<TLoginResponse>;

  /** Configuración de marca/entorno. */
  private _data: InitData;
  /** Elemento raíz de la app (loader → login/layout). */
  private _appElement!: HTMLDivElement;

  /** @param data Configuración de marca/entorno. */
  constructor(data: InitData) {
    this._data = data;
  }

  /** Aviso opcional (HTML ya escapado) que el layout muestra bajo el topbar,
   *  calculado con la respuesta del login. Por defecto no hay aviso; las
   *  subclases lo sobrescriben (p. ej. `ClienteInit`, vencimiento de licencia). */
  protected getAviso(_response: TLoginResponse): string | null {
    return null;
  }

  /** Arranca la app: transporte gRPC-Web → loader → error service → validación de token.
   *  Las marcas que entran por el portal de Nitria (`InitData.portalLoginUrl`)
   *  siguen su propio camino, sin pantalla de login. */
  public init() {
    document.body.dataset.brand = this._data.brandId;
    fx_initGrpcTransport(this._data);
    this._createMainDiv();
    errorService.init();
    if (this._data.portalLoginUrl) {
      this._initDesdePortal(this._data.portalLoginUrl);
      return;
    }
    this._checkToken();
  }

  /** Crea el elemento raíz con el loader (logo) y lo monta en el `body`. */
  private _createMainDiv() {
    this._appElement = document.createElement("div");
    this._appElement.className = "sx-loader";
    this._appElement.innerHTML = `<img src="${this._data.logoRoute}" alt="" />`;
    document.body.appendChild(this._appElement);
  }

  /** Valida el token de sesión: si existe y es válido monta el layout; si no,
   *  o si la validación falla, muestra el login. */
  private _checkToken() {
    secureStorage.get().then((token) => {
      if (!token) {
        this._showLogin();
        return;
      }

      new LoginByTokenGrpcService<TLoginResponse>()
        .saveAsync(fx_createLoginByTokenRequest(token))
        .then((r) => this._initLayout(r))
        .catch(() => this._showLogin());
    });
  }

  /** Arranque de una marca que entra solo por el portal de Nitria. En la
   *  ruta del portal inicia sesión con el token que trae; en cualquier otra,
   *  con la sesión guardada. Si el servidor rechaza la sesión con la app
   *  abierta, redirige al login del portal. */
  private _initDesdePortal(portalLoginUrl: string): void {
    grpcTransportProvider.onSesionRechazada(() =>
      fx_irAlPortalNitria(portalLoginUrl),
    );

    const url = new URL(window.location.href);
    if (url.pathname === PORTAL_NITRIA_RUTA_SSO) {
      const token = url.searchParams.get("token");
      // El token del portal no debe quedar en la barra de direcciones ni en el historial.
      window.history.replaceState(null, "", "/");
      this._entrarConTokenPortal(token, portalLoginUrl);
      return;
    }
    this._entrarConSesionGuardada(portalLoginUrl);
  }

  /** Inicia sesión con el token del portal y abre la app. Si no se puede,
   *  muestra el motivo exacto con un botón al login del portal. */
  private _entrarConTokenPortal(
    token: string | null,
    portalLoginUrl: string,
  ): void {
    const irAlPortal = {
      texto: PORTAL_NITRIA_BOTON,
      onClick: () => fx_irAlPortalNitria(portalLoginUrl),
    };
    if (!token) {
      fx_renderAccesoPortal(
        this._appElement,
        this._data.logoRoute,
        PORTAL_NITRIA_SIN_TOKEN_MESSAGE,
        irAlPortal,
      );
      return;
    }

    new LoginPortalGrpcService<TLoginResponse>()
      .loginAsync(token)
      .then((response) => this._initLayout(response))
      .catch((error: unknown) => {
        // Rechazo del servidor: su texto es el motivo. Falla de red: hay que pedir un enlace nuevo.
        const mensaje =
          error instanceof HttpError
            ? error.message
            : PORTAL_NITRIA_SIN_CONEXION_MESSAGE;
        fx_renderAccesoPortal(
          this._appElement,
          this._data.logoRoute,
          mensaje,
          irAlPortal,
        );
      });
  }

  /** Abre la app con la sesión guardada. Sin sesión, o si el servidor la
   *  rechaza (inexistente, vencida, cuenta inactiva o licencia), redirige al
   *  login del portal. Ante una falla de red o del servidor no redirige:
   *  muestra el error con un botón para reintentar, para no mandar al portal
   *  a quien solo perdió la señal. */
  private _entrarConSesionGuardada(portalLoginUrl: string): void {
    secureStorage.get().then((token) => {
      if (!token) {
        fx_irAlPortalNitria(portalLoginUrl);
        return;
      }

      new LoginByTokenGrpcService<TLoginResponse>()
        .saveAsync(fx_createLoginByTokenRequest(token))
        .then((response) => this._initLayout(response))
        .catch((error: unknown) => {
          if (
            error instanceof NotFoundError ||
            error instanceof UnauthorizedError
          ) {
            fx_irAlPortalNitria(portalLoginUrl);
            return;
          }
          const mensaje =
            error instanceof HttpError
              ? error.message
              : PORTAL_NITRIA_REINTENTAR_MESSAGE;
          fx_renderAccesoPortal(this._appElement, this._data.logoRoute, mensaje, {
            texto: "Reintentar",
            onClick: () => window.location.reload(),
          });
        });
    });
  }

  private _login: Login<TLoginResponse> | null = null;

  /** Muestra la pantalla de login; al autenticar, monta el layout. */
  private _showLogin(): void {
    this._appElement.id = "loginWrapper";
    this._appElement.className = "";
    this._login = new Login<TLoginResponse>(
      this._data.logoRoute,
      this._data.withRecuperarPassword,
      (response) => this._initLayout(response),
    );
    this._appElement.innerHTML = this._login.render();
    this._login.connect();
  }

  /** Monta el layout principal (`AppComponent`) con el menú y el logout. */
  private _initLayout(response: TLoginResponse) {
    this._login?.dispose();
    syncAllCatalogsInBackground();
    //
    const config: AppConfig<TLoginResponse> = {
      asideMenuRender: this.asideMenuRender,
      brand: this._data.brandRoute,
      defaultTitle: this._data.defaultTitle,
      loginResponse: response,
      aviso: this.getAviso(response),
    };
    const appRender = new AppRender<TLoginResponse>(config);
    this._appElement.className = "sx-app";
    this._appElement.id = "app";
    this._appElement.innerHTML = appRender.render();
    appRender.connect();
  }
}
